import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRightIcon, BellIcon, ChevronRightIcon } from 'lucide-react';
import { Avatar } from '../components/Avatar';
import { BrandMark } from '../components/BrandMark';
import { HydrationMeter } from '../components/HydrationMeter';
import { PendingHabits } from '../components/PendingHabits';
import { Button } from '../components/ui/Button';
import { Card, SectionTitle } from '../components/ui/Card';
import { DailySummaryCard } from '../components/home/DailySummaryCard';
import { findPerson, people } from '../data/people';
import { currentUser } from '../data/user';
import { useLocale } from '../contexts/LocaleContext';
import { useProgress } from '../contexts/ProgressContext';
import { useSocial } from '../contexts/SocialContext';
import { average, buildHistory } from '../utils/history';
import { bandKey, greetingKey } from '../utils/progress';

/**
 * MOB/Home — PERSONAL.
 * Greeting → Life Score → Daily Check → Bee today → Streak → Pending →
 * Weekly insight → a small Community teaser. The social feed lives in
 * Community, never here.
 */
export function Home() {
  const {
    t,
    dayUnit
  } = useLocale();
  const navigate = useNavigate();
  const progress = useProgress();
  const social = useSocial();
  const me = people[0];
  const history = useMemo(() => buildHistory(90, currentUser.streak), []);
  const week = useMemo(() => [...history.slice(-6), {
    date: new Date(),
    score: progress.todayScore
  }], [history, progress.todayScore]);
  const weekAverage = average(week);
  const strongDays = week.filter(day => day.score >= 70).length;
  const unseenPulses = social.pulses.filter(ring => ring.authorId !== 'me' && !ring.seen);
  const dailyMessage = progress.isFirstUse ? t('home.message.first') : progress.dayComplete ? t('home.message.complete', {
    total: progress.baseTotalXp
  }) : progress.mood === 'drained' ? t('home.message.rest') : progress.dailyXp === 0 ? t('home.message.start') : progress.dailyXp >= progress.postGateXp ? t('home.message.past50') : t('home.message.going', {
    n: progress.remainingXp
  });
  const streakHint = progress.nextBadge ? t('home.streak.next', {
    badge: t(progress.nextBadge.id),
    n: progress.daysToNextBadge
  }) : t('home.milestone.max');
  return <div className="pb-2" data-id="element-3094" data-mp="23bgqh5a0s37v">
      {/* Compact header */}
      <header className="flex items-center justify-between px-2 pt-2" data-id="element-3095" data-mp="23bqewoff22bg">
        <BrandMark size={26} data-id="element-3096" data-mp="2899upsrrin5i" />
        <div className="flex items-center gap-1" data-id="element-3097" data-mp="289i8dmvplwhr">
          <button type="button" onClick={() => navigate('/notificacoes')} aria-label={t('common.notifications')} className="relative grid h-11 w-11 place-items-center rounded-pill text-deep transition-colors duration-150 ease-brand hover:bg-sand-deep" data-id="element-3098" data-mp="1tlx8s2y1mfel">
            <BellIcon size={19} data-id="element-3099" data-mp="2r12rzzdv6uj1" />
            <span className="absolute right-[12px] top-[11px] h-[7px] w-[7px] rounded-pill bg-gold" aria-hidden="true" data-id="element-3100" data-mp="2r241w0j59az6" />
          </button>
          <Avatar src={me.avatar} name={me.name} size={32} onClick={() => navigate('/perfil')} label={t('nav.profile')} data-id="element-3101" data-mp="alxzf8bpil4d" />
        </div>
      </header>

      {/* 1 — Greeting */}
      <div className="px-2 pb-2 pt-1" data-id="element-3102" data-mp="3sscb10mi6izl">
        <h1 className="text-[22px] font-bold leading-tight text-deep" data-id="element-3103" data-mp="3stdmh67fl7hu">
          {t(greetingKey())}, {currentUser.firstName}.
        </h1>
        <p className="mt-[2px] text-[13px] font-medium leading-snug text-muted" data-id="element-3104" data-mp="3nvc4yppnlg8h">
          {dailyMessage}
        </p>
      </div>

      {progress.isFirstUse ? <div className="px-2" data-id="element-3105" data-mp="ni3ms1qinoj">
          <Card tone="dark" className="rounded-panel p-3" data-id="element-3106" data-mp="qnvgf1r03ny">
            <p className="bee-overline text-[9px] font-bold text-gold" data-id="element-3107" data-mp="1h1rtxrpjemi0">
              {t('check.title')}
            </p>
            <h2 className="mt-2 text-title text-sand" data-id="element-3108" data-mp="1c36sjoyjtwrz">{t('home.firstUse.title')}</h2>
            <p className="mt-1 text-[13px] leading-relaxed text-ondark" data-id="element-3109" data-mp="27ey5zluepftb">
              {t('home.firstUse.body')}
            </p>
            <Button variant="gold" size="lg" fullWidth className="mt-3" onClick={() => navigate('/check')} data-id="element-3110" data-mp="2cd81xynygkfz">
              {t('home.firstUse.cta')}
              <ArrowRightIcon size={17} data-id="element-3111" data-mp="2qd0r3pju2slu" />
            </Button>
          </Card>
        </div> : <>
          {/* 2 · 4 · 5 — Life Score, with Bee today and streak alongside */}
          <div className="px-2" data-id="element-3112" data-mp="302k8srvt8gf4">
            <DailySummaryCard score={progress.todayScore} total={progress.baseTotalXp} bandLabel={t(bandKey(progress.band))} stats={[{
          label: t('home.summary.check'),
          value: `${progress.completedCount}/${progress.habits.length}`
        }, {
          label: t('home.summary.nectar'),
          value: `${progress.dailyXp}`,
          hint: `/ ${progress.baseTotalXp}`
        }, {
          label: t('home.summary.streak'),
          value: `${progress.streak} ${dayUnit(progress.streak)}`,
          hint: streakHint
        }]} data-id="element-3113" data-mp="303lnd6c1bl1l" />
          </div>

          {/* 3 · 6 — Daily Check with what is still pending */}
          <div className="mt-2 px-2" data-id="element-3114" data-mp="1h3w5k3qe65z6">
            <Card className="p-2" data-id="element-3115" data-mp="1h4xheaf8ewhy">
              <div className="flex items-baseline justify-between gap-2" data-id="element-3116" data-mp="1h4nzla4cbvnc">
                <p className="min-w-0 text-[13.5px] font-medium text-deep" data-id="element-3117" data-mp="1cbux6swcgzef">
                  {t('home.check.progress', {
                done: progress.completedCount,
                total: progress.habits.length
              })}
                </p>
                <p className="bee-num shrink-0 text-[12px] font-bold text-gold-deep" data-id="element-3118" data-mp="2028fvst1hlwc">
                  {progress.dayComplete ? t('home.check.allDone') : t('home.check.remaining', {
                n: progress.remainingXp
              })}
                </p>
              </div>

              <div className="mt-2 border-t border-sand-line pt-2" data-id="element-3119" data-mp="3kl13347fuugi">
                <HydrationMeter variant="compact" data-id="element-3120" data-mp="3kioon10w3287" />
              </div>

              {progress.pendingHabits.length > 0 && <div className="mt-2" data-id="element-3121" data-mp="3amv2qr1a5wdr">
                  <PendingHabits habits={progress.pendingHabits} onOpen={() => navigate('/check')} data-id="element-3122" data-mp="3altqij8j33ug" />
                </div>}

              <Button variant={progress.dayComplete ? 'outline' : 'gold'} size="lg" fullWidth className="mt-2" onClick={() => navigate('/check')} data-id="element-3123" data-mp="31myhkze3nfng">
                {progress.dayComplete ? t('home.check.ctaDone') : progress.completedCount === 0 ? t('home.check.ctaStart') : t('home.check.cta')}
                <ArrowRightIcon size={17} data-id="element-3124" data-mp="19yqu7jexq9bh" />
              </Button>
            </Card>
          </div>

          {/* Low-energy day keeps the gentle goals */}
          {progress.mood === 'drained' && <div className="mt-2 px-2" data-id="element-3125" data-mp="1izf6a8krpzlq">
              <Card className="border-gold/40 bg-sand-deep/50 p-2" data-id="element-3126" data-mp="1iydlvdhv40rc">
                <h2 className="text-section text-deep" data-id="element-3127" data-mp="1xnjozcjtmb4i">{t('home.gentle.title')}</h2>
                <p className="mt-[2px] text-[12px] text-muted" data-id="element-3128" data-mp="1sp8h37d36aox">
                  {t('home.gentle.body')}
                </p>
                <ul className="mt-2 flex flex-wrap gap-x-3 gap-y-1" data-id="element-3129" data-mp="26nq1ow7p372m">
                  {['1', '2', '3', '4'].map(key => <li key={key} className="flex items-center gap-1 text-[12.5px] text-deep" data-id="element-3130" data-mp="22no1yslisnpz">
                      <span className="h-[5px] w-[5px] shrink-0 rounded-pill bg-gold" aria-hidden="true" data-id="element-3131" data-mp="89wrxrza41u0" />
                      {t(`home.gentle.${key}`)}
                    </li>)}
                </ul>
              </Card>
            </div>}

          {/* 7 — Weekly personal insight */}
          <section className="mt-3 px-2" data-id="element-3132" data-mp="1al86u134fuku">
            <SectionTitle action={<button type="button" onClick={() => navigate('/stats')} className="text-[11px] font-medium text-gold-deep transition-colors duration-150 ease-brand hover:text-deep" data-id="element-3134" data-mp="dvh093hk2gyo">
                  {t('home.insight.cta')}
                </button>} data-id="element-3133" data-mp="1akyg2ba95je3">
              {t('home.insight.title')}
            </SectionTitle>
            <Card className="p-2" data-id="element-3135" data-mp="1ybkfje66xvch">
              <div className="flex h-[52px] items-end gap-[6px]" aria-hidden="true" data-id="element-3136" data-mp="1yaixuquo6als">
                {week.map((day, index) => <span key={index} className={`flex-1 rounded-t-[4px] ${index === week.length - 1 ? 'bg-gold' : day.score >= 70 ? 'bg-soft' : 'bg-sand-deep'}`} style={{
              height: `${Math.max(8, day.score)}%`
            }} data-id="element-3137" data-mp="23byjak0rej4p" data-mpi={index} />)}
              </div>
              <p className="mt-2 text-[13px] font-medium text-deep" data-id="element-3138" data-mp="1uovz9ffih2ap">
                {t('home.insight.avg', {
              n: weekAverage
            })}
              </p>
              <p className="mt-[2px] text-[11.5px] text-muted" data-id="element-3139" data-mp="1kp56pjaigxd9">
                {t('home.insight.strong', {
              n: strongDays
            })}
              </p>
            </Card>
          </section>

          {/* 8 — Small Community teaser, always last */}
          <section className="mt-3 px-2" data-id="element-3140" data-mp="3f2w9byufagz7">
            <button type="button" onClick={() => navigate('/comunidade')} className="flex min-h-[56px] w-full items-center gap-2 rounded-card border border-sand-line bg-white/50 px-2 py-2 text-left transition-colors duration-150 ease-brand hover:border-deep/25" data-id="element-3141" data-mp="3b1uio6eknd8u">
              <span className="flex shrink-0 -space-x-2" data-id="element-3142" data-mp="mnaq8eps6zxn">
                {unseenPulses.slice(0, 3).map(ring => {
              const author = findPerson(ring.authorId);
              return <span key={ring.id} className="rounded-pill bg-sand p-[2px]" data-id="element-3143" data-mp="3q2pzhdqc55hq">
                      <Avatar src={author.avatar} name={author.name} size={26} data-id="element-3144" data-mp="3l4oh6v0qha7b" />
                    </span>;
            })}
              </span>
              <span className="min-w-0 flex-1" data-id="element-3145" data-mp="3pztb0rmgild">
                <span className="block text-[12.5px] font-medium text-deep" data-id="element-3146" data-mp="3v2ay1n1lfbtx">
                  {t(unseenPulses.length === 1 ? 'home.teaser.titleOne' : 'home.teaser.title', {
                n: unseenPulses.length
              })}
                </span>
                <span className="block text-[11.5px] leading-snug text-muted" data-id="element-3147" data-mp="1hv3041b3i748">
                  {t('home.teaser.body')}
                </span>
              </span>
              <ChevronRightIcon size={17} className="shrink-0 text-muted" aria-hidden="true" data-id="element-3148" data-mp="18vpwvqdkeery" />
            </button>
          </section>
        </>}
    </div>;
}